// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — useLubpressData
// Hook de dados EXCLUSIVO para o contexto Lubpress/Vibra.
// Carrega apenas: vibra_tabelas, vibra_rotas, lubpress_ctes.
// Não toca em tabelas_frete, municipios, clientes ou notas_fiscais
// da Gamma — os dois contextos ficam completamente isolados.
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";

export function useLubpressData(enabled = true) {
  const [tabelasVibra,  setTabelasVibraSt] = useState([]);
  const [rotasVibra,    setRotasVibraSt]  = useState([]);
  const [ctes,          setCtes]          = useState([]);
  const [notas,         setNotas]         = useState([]);
  const [produtos,      setProdutos]      = useState([]);
  const [configPaletes, setConfigPaletes] = useState([]);
  const [paletizados,   setPaletizados]   = useState([]);
  const [clientes,      setClientesState]  = useState([]);
  const [municipiosLub, setMunicipiosLubSt] = useState([]);
  const [tabelasLub,    setTabelasLubSt]    = useState([]);
  // Tabelas Gamma — necessárias para calcular custo dos prestadores
  const [transportadoras, setTransportadorasState] = useState([]);
  const [tabelasGamma,  setTabelasGamma]  = useState([]);
  const [municipiosGamma, setMunicipiosGamma] = useState([]);
  const [armazenagemNotas, setArmazenagemNotasSt] = useState([]);
  const [retornosRateio,   setRetornosRateioSt]   = useState([]);
  const [geracoes, setGeracoesSt] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [erro,          setErro]          = useState(null);
  const [salvando,      setSalvando]      = useState(false);
  const [erroSalvar,    setErroSalvar]    = useState(null);

  // ── Carga inicial ──────────────────────────────────────────────
  const carregar = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setErro(null);

    try {
      const [resTab, resRot, resCtes, resNotas, resProdutos, resCfgPal, resPaletz, resTransp, resCli, resMunLub, resTabelasLub, resGammaTab, resGammaMun, resArmaz, resRetorno, resGeracoes] = await Promise.all([
        supabase.from("vibra_tabelas").select("*"),
        supabase.from("vibra_rotas").select("*"),
        supabase.from("lubpress_ctes").select("*")
          .order("data_emissao", { ascending: false })
          .limit(2000),
        supabase.from("lubpress_notas").select("*")
          .order("data_emissao", { ascending: false })
          .limit(2000),
        supabase.from("lubpress_produtos").select("*").order("codigo"),
        supabase.from("lubpress_config_paletes").select("*"),
        supabase.from("lubpress_paletizados").select("*"),
        supabase.from("lubpress_transportadoras").select("*").order("codigo"),
        supabase.from("lubpress_clientes").select("*").order("nome"),
        supabase.from("lubpress_municipios").select("*").order("mun"),
        supabase.from("lubpress_tabelas_frete").select("*").order("cod"),
        supabase.from("tabelas_frete").select("*").order("cod"),
        supabase.from("municipios").select("*").order("mun"),
        supabase.from("lubpress_armazenagem_notas").select("*").order("n_nf"),
        supabase.from("lubpress_retornos_rateio").select("*")
          .order("created_at", { ascending: false }).limit(5000),
        supabase.from("lubpress_geracoes").select("*")
          .order("gerado_em", { ascending: false }).limit(3000),
      ]);
      if (!resGeracoes.error) setGeracoesSt(resGeracoes.data || []);
      if (resTab.error)      throw new Error("vibra_tabelas: " + resTab.error.message);
      if (resRot.error)      throw new Error("vibra_rotas: "   + resRot.error.message);
      if (resCtes.error)     throw new Error("lubpress_ctes: " + resCtes.error.message);
      // tabelas/municipios Gamma: falha silenciosa (não bloqueia o contexto Lubpress)
      if (!resGammaTab.error) setTabelasGamma(resGammaTab.data || []);
      if (!resGammaMun.error) setMunicipiosGamma(resGammaMun.data || []);
      if (!resNotas.error)    setNotas(resNotas.data || []);
      if (!resProdutos.error) setProdutos(resProdutos.data || []);
      if (!resCfgPal.error)  setConfigPaletes(resCfgPal.data  || []);
      if (!resPaletz.error)  setPaletizados(resPaletz.data    || []);
      if (!resTransp.error)  setTransportadorasState(
        (resTransp.data || []).map(t => ({
          ...t,
          // Normaliza: se veio só com colunas antigas, preenche as novas e vice-versa
          codigo:        t.codigo        || t.cod   || null,
          nome_curto:    t.nome_curto    || t.short  || null,
          nome_completo: t.nome_completo || t.nome   || null,
          cod:           t.cod   || t.codigo        || null,
          short:         t.short || t.nome_curto    || null,
          nome:          t.nome  || t.nome_completo || null,
        }))
      );
      if (!resCli.error)     setClientesState(resCli.data || []);
      if (!resMunLub.error)  setMunicipiosLubSt(resMunLub.data || []);
      if (!resTabelasLub.error) setTabelasLubSt(
        (resTabelasLub.data || []).map(r => ({
          id:r.id, cod:r.cod, codTransp:r.cod_transp||"", transp:r.transp||"",
          tipo:r.tipo||"PCT", modal:r.modal||"Rodoviário",
          vigIni:r.vig_ini||"", vigFim:r.vig_fim||"",
          adv:r.adv||0, advMin:r.adv_min||0, pgv:r.pgv||0, rkg:r.rkg||0,
          minimo:r.minimo||0, gris:r.gris||0, grisMin:r.gris_min||0,
          desp:r.desp||0, tas:r.tas||0, tad:r.tad||0, ped:r.ped||0,
          rdc:r.rdc||0, rdcTipo:r.rdc_tipo||"percNota", icms:r.icms||0,
          excedente:r.excedente||0, frac:r.frac||"Por peso real", pedMin:r.ped_min||0,
          rotas:r.rotas||[], faixas:r.faixas||[],
        }))
      );

      setTabelasVibraSt(resTab.data || []);
      setRotasVibraSt(resRot.data  || []);
      setCtes(resCtes.data         || []);
      if (!resArmaz.error)   setArmazenagemNotasSt(resArmaz.data   || []);
      if (!resRetorno.error) setRetornosRateioSt(resRetorno.data   || []);
    } catch (e) {
      console.error("[Lubpress]", e.message);
      setErro(e.message);
    }

    setLoading(false);
  }, [enabled]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Salvar CT-e (receita ou custo) ─────────────────────────────
  async function salvarCte(cte) {
    setSalvando(true); setErroSalvar(null);
    const row = {
      id:              cte.id || "cte-" + Date.now(),
      tipo:            cte.tipo,
      filial:          cte.filial,
      num_cte:         cte.numCte,
      chave_cte:       cte.chaveCte   || null,
      data_emissao:    cte.dataEmissao,
      data_entrega:    cte.dataEntrega || null,
      num_nf_vibra:    cte.numNfVibra || null,
      cidade_destino:  cte.cidadeDestino,
      uf_destino:      cte.ufDestino,
      cod_ibge_destino: cte.codIbgeDestino || null,
      rota:            cte.rota,
      tipo_carga:      cte.tipoCarga,
      peso_kg:         cte.pesoKg      || null,
      volume_litros:   cte.volumeLitros|| null,
      valor_nf:        cte.valorNf     || null,
      valor_cobrado:   cte.valorCobrado|| null,
      valor_calculado: cte.valorCalculado|| null,
      diff_receita:    cte.diffReceita || null,
      prestador:       cte.prestador   || null,
      valor_custo:     cte.valorCusto  || null,
      margem:          cte.margem      || null,
      comps_xml:       cte.compsXml && Object.keys(cte.compsXml).length ? cte.compsXml : null,
      cte_receita_id:  cte.cteReceitaId|| null,
      status_otif:     cte.statusOtif  || null,
      dias_atraso:     cte.diasAtraso  || null,
      obs:             cte.obs         || null,
    };

    const { error } = await supabase
      .from("lubpress_ctes")
      .upsert(row, { onConflict: "id" });

    if (error) {
      setErroSalvar("Erro ao salvar CT-e: " + error.message);
      setSalvando(false);
      throw error;
    }

    // Atualiza cache local
    setCtes(prev => {
      const i = prev.findIndex(c => c.id === row.id);
      return i >= 0 ? prev.map((c, idx) => idx === i ? row : c) : [row, ...prev];
    });

    setSalvando(false);
    setErroSalvar(null);
  }

  // ── Salvar lote de CT-es (importação de XML) ───────────────────
  async function salvarCtes(lista) {
    setSalvando(true); setErroSalvar(null);
    const rows = lista.map(cte => ({
      id:              cte.id || "cte-" + Date.now() + Math.random(),
      tipo:            cte.tipo,
      filial:          cte.filial,
      num_cte:         cte.numCte,
      chave_cte:       cte.chaveCte    || null,
      data_emissao:    cte.dataEmissao,
      data_embarque:   cte.dataEmbarque || null,
      data_entrega:    cte.dataEntrega || null,
      num_nf_vibra:    cte.numNfVibra  || null,
      cidade_destino:  cte.cidadeDestino,
      uf_destino:      cte.ufDestino,
      cod_ibge_destino: cte.codIbgeDestino || null,
      rota:            cte.rota,
      tipo_carga:      cte.tipoCarga,
      peso_kg:         cte.pesoKg      || null,
      volume_litros:   cte.volumeLitros|| null,
      valor_nf:        cte.valorNf     || null,
      valor_cobrado:   cte.valorCobrado|| null,
      valor_calculado: cte.valorCalculado|| null,
      diff_receita:    cte.diffReceita || null,
      prestador:       cte.prestador   || null,
      valor_custo:     cte.valorCusto  || null,
      margem:          cte.margem      || null,
      comps_xml:       cte.compsXml && Object.keys(cte.compsXml).length ? cte.compsXml : null,
      cte_receita_id:  cte.cteReceitaId|| null,
      status_otif:     cte.statusOtif  || null,
      dias_atraso:     cte.diasAtraso  || null,
      obs:             cte.obs         || null,
    }));

    // Resiliente: se colunas novas (comps_xml, data_embarque) ainda não
    // existirem no banco, remove-as e repete (rode as migrations
    // 20260722000001_lubpress_ctes_comps.sql e 20260723000002_lubpress_ctes_embarque.sql).
    let semNovas = false;
    const semColsNovas = (r) => { const { comps_xml, data_embarque, ...resto } = r; return resto; }; // eslint-disable-line no-unused-vars
    for (let i = 0; i < rows.length; i += 100) {
      const lote = rows.slice(i, i + 100);
      let { error } = await supabase
        .from("lubpress_ctes")
        .upsert(semNovas ? lote.map(semColsNovas) : lote, { onConflict: "id" });
      if (error && !semNovas && /comps_xml|data_embarque|column/i.test(error.message || "")) {
        console.warn("[TMS] Colunas comps_xml/data_embarque ausentes em lubpress_ctes — salvando sem elas. Rode as migrations de CT-e.");
        semNovas = true;
        ({ error } = await supabase
          .from("lubpress_ctes")
          .upsert(lote.map(semColsNovas), { onConflict: "id" }));
      }
      if (error) {
        setErroSalvar("Erro ao salvar lote: " + error.message);
        setSalvando(false);
        throw error;
      }
    }

    await carregar(); // recarrega para refletir todos os dados
    setSalvando(false);
    setErroSalvar(null);
  }

  // ── Salvar Notas Fiscais da Vibra ──────────────────────────────
  async function salvarNotas(lista) {
    const rows = lista.map(n => ({
      id:                 n.id,
      chave_nfe:          n.chave_nfe          || null,
      num_nf:             n.num_nf,
      serie:              n.serie              || null,
      filial:             n.filial             || null,
      // Origem: "vibra" (padrão) ou "gamma_comercio" (só custo, fora dos
      // cálculos da Vibra). Requer as colunas origem/emit_* em lubpress_notas
      // (migration 20260722). Se ainda não existirem, o fallback abaixo salva
      // sem elas para não quebrar o import.
      origem:             n.origem             || "vibra",
      emit_cnpj:          n.emit_cnpj          || null,
      emit_nome:          n.emit_nome          || null,
      data_emissao:       n.data_emissao,
      cidade_destino:     n.cidade_destino     || null,
      uf_destino:         n.uf_destino         || null,
      cod_ibge:           n.cod_ibge           || null,
      cod_produto:        n.cod_produto        || null,
      peso_kg:            n.peso_kg            ?? null,
      volume_litros:      n.volume_litros      ?? null,
      valor_nf:           n.valor_nf           ?? null,
      tipo_carga:         n.tipo_carga,
      tem_cte:            n.tem_cte            || false,
      cte_id:             n.cte_id             || null,
      // Campos operacionais — romaneio, agrupamento, etiquetas
      qvol:              n.qvol               ?? null,
      nome_destinatario: n.nome_destinatario  || null,
      cnpj_dest:         n.cnpj_dest          || null,
      bairro_dest:       n.bairro_dest        || null,
      // Número de transporte e itens da NF (para romaneio)
      num_transporte:     n.num_transporte     || null,
      itens:              n.itens?.length ? n.itens : null,
      // Campos de entrega e OTIF duplo
      data_embarque:      n.data_embarque      || null,
      data_entrega:       n.data_entrega       || null,
      prestador:          n.prestador          || null,
      prazo_vibra:        n.prazo_vibra        ?? null,
      prazo_transp:       n.prazo_transp       ?? null,
      status_otif_vibra:  n.status_otif_vibra  || null,
      status_otif_transp: n.status_otif_transp || null,
      dias_atraso_vibra:  n.dias_atraso_vibra  ?? null,
      dias_atraso_transp: n.dias_atraso_transp ?? null,
      // Endereço de entrega estruturado (tag <entrega>)
      end_entrega_logr:   n.end_entrega_logr   || null,
      end_entrega_nro:    n.end_entrega_nro    || null,
      end_entrega_bairro: n.end_entrega_bairro || null,
      end_entrega_mun:    n.end_entrega_mun    || null,
      end_entrega_uf:     n.end_entrega_uf     || null,
      end_entrega_cep:    n.end_entrega_cep    || null,
      // Status de embarque e texto complementar
      status_embarque:    n.status_embarque    || "ok",
      motivo_status:      n.motivo_status      || null,
      inf_complementar:   n.inf_complementar   || null,
      obs:                n.obs                || null,
      // Retirada FOB — transportador literal do XML e modalidade de frete
      mod_frete:              n.mod_frete              || null,
      cnpj_transportador_xml: n.cnpj_transportador_xml || null,
      nome_transportador_xml: n.nome_transportador_xml || null,
      retirada_fob:            n.retirada_fob           || false,
    }));
    // Resiliente: se as colunas origem/emit_* ainda não existirem no banco,
    // remove-as e repete (rode a migration 20260722000002_lubpress_notas_origem.sql).
    let semOrigem = false;
    const semColsOrigem = (r) => {
      const { origem, emit_cnpj, emit_nome, ...resto } = r; // eslint-disable-line no-unused-vars
      return resto;
    };
    for (let i = 0; i < rows.length; i += 100) {
      const lote = rows.slice(i, i + 100);
      let { error } = await supabase.from("lubpress_notas")
        .upsert(semOrigem ? lote.map(semColsOrigem) : lote, { onConflict: "id" });
      if (error && !semOrigem && /origem|emit_cnpj|emit_nome|column/i.test(error.message || "")) {
        console.warn("[TMS] Colunas origem/emit_* ausentes em lubpress_notas — salvando sem elas. Rode a migration 20260722000002_lubpress_notas_origem.sql");
        semOrigem = true;
        ({ error } = await supabase.from("lubpress_notas")
          .upsert(lote.map(semColsOrigem), { onConflict: "id" }));
      }
      if (error) throw error;
    }
    await carregar();
  }

  // ── Excluir CT-e ───────────────────────────────────────────────
  async function setProdutosLub(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(produtos) : novasOuFn;
    setProdutos(novas);
    const rows = novas.map(p => ({
      id:              p.id || p.codigo,
      codigo:          p.codigo,
      descricao:       p.descricao,
      unidade:         p.unidade,
      end_estoque_cwb: p.end_estoque_cwb || null,
      end_estoque_rbp: p.end_estoque_rbp || null,
      ativo:           p.ativo !== false,
    }));
    for (let i = 0; i < rows.length; i += 200) {
      const { error } = await supabase.from("lubpress_produtos")
        .upsert(rows.slice(i, i + 200), { onConflict: "id" });
      if (error) throw error;
    }
  }

  async function setTabelasLub(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(tabelasLub) : novasOuFn;
    setTabelasLubSt(novas);
    const rows = novas.map(t => ({
      id:           t.id || t.cod,
      cod:          t.cod,
      cod_transp:   t.codTransp   || null,
      transp:       t.transp      || null,
      tipo:         t.tipo        || "PCT",
      modal:        t.modal       || "Rodoviário",
      vig_ini:      t.vigIni      || null,
      vig_fim:      t.vigFim      || null,
      adv:          t.adv         ?? 0,
      adv_min:      t.advMin      ?? 0,
      pgv:          t.pgv         ?? 0,
      rkg:          t.rkg         ?? 0,
      minimo:       t.minimo      ?? 0,
      gris:         t.gris        ?? 0,
      gris_min:     t.grisMin     ?? 0,
      desp:         t.desp        ?? 0,
      tas:          t.tas         ?? 0,
      tad:          t.tad         ?? 0,
      ped:          t.ped         ?? 0,
      ped_min:      t.pedMin      ?? 0,
      rdc:          t.rdc         ?? 0,
      rdc_tipo:     t.rdcTipo     || "percNota",
      icms:         t.icms        ?? 0,
      excedente:    t.excedente   ?? 0,
      frac:         t.frac        || "Por peso real",
      rotas:        t.rotas       || [],
      faixas:       t.faixas      || [],
    }));
    for (let i = 0; i < rows.length; i += 100) {
      const { error } = await supabase.from("lubpress_tabelas_frete")
        .upsert(rows.slice(i, i + 100), { onConflict: "id" });
      if (error) throw error;
    }
    await carregar();
  }

  async function setMunicipiosLub(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(municipiosLub) : novasOuFn;
    setMunicipiosLubSt(novas);
    const rows = novas.map(m => ({
      id:                   m.id,
      mun:                  m.mun,
      uf:                   m.uf,
      filial:               m.filial               || null,
      transp_padrao:        m.transp_padrao         || null,
      prazo_padrao:         m.prazo_padrao          ?? null,
      transp_mec:           m.transp_mec            || null,
      prazo_mec:            m.prazo_mec             ?? null,
      transp_rural:         m.transp_rural          || null,
      prazo_rural:          m.prazo_rural           ?? null,
      prazo_transp_prometido: m.prazo_transp_prometido ?? null,
      cod_ibge:             m.cod_ibge              || null,
    }));
    for (let i = 0; i < rows.length; i += 200) {
      const { error } = await supabase.from("lubpress_municipios")
        .upsert(rows.slice(i, i + 200), { onConflict: "id" });
      if (error) throw error;
    }
    await carregar();
  }

  // ── Salvar tabelas Vibra (tarifas por rota, usado no cálculo do CT-e) ──
  // id = rota + vig_ini, porque uma mesma rota pode ter várias faixas de
  // vigência ao longo do tempo (reajuste de tarifa não sobrescreve a antiga).
  async function setVibraTabelas(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(tabelasVibra) : novasOuFn;
    setTabelasVibraSt(novas);
    const rows = novas.map(t => ({
      id:             t.id || `${t.rota}_${t.vig_ini || "atual"}`,
      rota:           t.rota,
      uf:             (t.uf || "").toUpperCase().trim(),
      tipo:           t.tipo || "solta",
      vig_ini:        t.vig_ini || null,
      vig_fim:        t.vig_fim || null,
      sla_dias_uteis: t.sla_dias_uteis ?? null,
      peso_min:       t.peso_min ?? null,
      f1_peso_ate:    t.f1_peso_ate ?? null,
      f1_valor:       t.f1_valor ?? null,
      f2_peso_ate:    t.f2_peso_ate ?? null,
      f2_valor:       t.f2_valor ?? null,
      f3_peso_ate:    t.f3_peso_ate ?? null,
      f3_valor:       t.f3_valor ?? null,
      f4_peso_ate:    t.f4_peso_ate ?? null,
      f4_valor:       t.f4_valor ?? null,
      f5_valor:       t.f5_valor ?? null,
      gris_pct:       t.gris_pct ?? null,
      adv_pct:        t.adv_pct ?? null,
      ped_por_ton:    t.ped_por_ton ?? null,
      vol_min_litros: t.vol_min_litros ?? null,
      frete_por_m3:   t.frete_por_m3 ?? null,
      ped_por_m3:     t.ped_por_m3 ?? null,
    }));
    for (let i = 0; i < rows.length; i += 200) {
      const { error } = await supabase.from("vibra_tabelas")
        .upsert(rows.slice(i, i + 200), { onConflict: "id" });
      if (error) throw error;
    }
    await carregar();
  }

  // ── Salvar rotas Vibra (cidade → código de rota, usado no CT-e) ────
  async function setRotasVibra(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(rotasVibra) : novasOuFn;
    setRotasVibraSt(novas);
    const rows = novas.map(r => ({
      id:             r.id || `${(r.cidade || "").toUpperCase().trim()}-${(r.uf || "").toUpperCase().trim()}`.replace(/\s+/g, "_"),
      cidade:         (r.cidade || "").toUpperCase().trim(),
      uf:             (r.uf || "").toUpperCase().trim(),
      rota_solta:     r.rota_solta  || null,
      rota_granel:    r.rota_granel || null,
      sla_dias_uteis: r.sla_dias_uteis ?? null,
    }));
    for (let i = 0; i < rows.length; i += 200) {
      const { error } = await supabase.from("vibra_rotas")
        .upsert(rows.slice(i, i + 200), { onConflict: "id" });
      if (error) throw error;
    }
    await carregar();
  }

  async function excluirCte(id) {
    const { error } = await supabase
      .from("lubpress_ctes").delete().eq("id", id);
    if (error) throw error;
    setCtes(prev => prev.filter(c => c.id !== id));
  }

  async function setClientes(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(clientes) : novasOuFn;
    setClientesState(novas);
    const rows = novas.map(c => ({
      id:         c.id,
      cnpj:       c.cnpj       || null,
      nome:       c.nome,
      cidade:     c.cidade     || null,
      uf:         c.uf         || null,
      filial:     c.filial     || null,
      paletizado: c.paletizado || false,
      lat:        c.lat        || null,
      lon:        c.lon        || null,
    }));
    for (let i = 0; i < rows.length; i += 200) {
      const { error } = await supabase.from("lubpress_clientes")
        .upsert(rows.slice(i, i + 200), { onConflict: "id" });
      if (error) throw error;
    }
    await carregar();
  }

  async function setTransportadoras(novasOuFn) {
    const novas = typeof novasOuFn === "function" ? novasOuFn(transportadoras) : novasOuFn;
    setTransportadorasState(novas);
    const rows = novas.map(t => ({
      id:            t.id || t.codigo || t.cod,
      // Colunas novas (módulo LubTransportadoras)
      codigo:        t.codigo        || t.cod   || null,
      nome_curto:    t.nome_curto    || t.short  || null,
      nome_completo: t.nome_completo || t.nome   || null,
      // Colunas antigas (tabela original — mantidas para compatibilidade)
      cod:           t.cod   || t.codigo        || null,
      short:         t.short || t.nome_curto    || null,
      nome:          t.nome  || t.nome_completo || null,
      filial:        t.filial || null,
      ativo:         t.ativo !== false,
      email_to:      t.email_to || null,
      email_cc:      t.email_cc || null,
    }));
    for (let i = 0; i < rows.length; i += 100) {
      const { error } = await supabase.from("lubpress_transportadoras")
        .upsert(rows.slice(i, i + 100), { onConflict: "id" });
      if (error) throw error;
    }
    await carregar();
  }

  // ── Armazenagem: importar NFs de entrada (nunca edita depois de salva) ─
  // Ignora NF inteira se já existir (mesmo comportamento do VBA: dicNFsExist
  // por número de NF, não por item — se a NF já foi importada, pula ela toda).
  async function salvarArmazenagemNotas(novasLinhas) {
    setSalvando(true); setErroSalvar(null);
    try {
      const nfsJaExistentes = new Set(armazenagemNotas.map(n => String(n.n_nf)));
      const linhas = (novasLinhas || []).filter(n => !nfsJaExistentes.has(String(n.n_nf)));
      const rows = linhas.map(n => ({
        id:             n.id || `${n.n_nf}-${n.n_item || "0"}`,
        n_nf:           n.n_nf,
        n_item:         n.n_item || null,
        n_fat:          n.n_fat || null,
        cod_produto:    n.cod_produto,
        desc_produto:   n.desc_produto || null,
        unidade:        n.unidade || null,
        qtd_recebida:   n.qtd_recebida || 0,
        valor_unitario: n.valor_unitario || 0,
        filial:         n.filial || null,
      }));
      for (let i = 0; i < rows.length; i += 200) {
        const { error } = await supabase.from("lubpress_armazenagem_notas")
          .upsert(rows.slice(i, i + 200), { onConflict: "id" });
        if (error) throw error;
      }
      await carregar();
      return { importadas: rows.length, ignoradas: (novasLinhas || []).length - linhas.length };
    } catch (e) {
      setErroSalvar(e.message);
      throw e;
    } finally {
      setSalvando(false);
    }
  }

  // ── Ledger de retornos: grava os lançamentos de um rateio ────────
  // Nunca edita lubpress_armazenagem_notas — só insere linhas novas aqui.
  // O saldo de cada NF é sempre calculado no front (qtd_recebida - soma
  // dos lançamentos), nunca decrementado in-place.
  async function salvarRetornosRateio(lancamentos) {
    setSalvando(true); setErroSalvar(null);
    try {
      const rows = (lancamentos || []).map((l, i) => ({
        id:                    l.id || `ret-${Date.now()}-${i}`,
        nota_armazenagem_id:   l.nota_armazenagem_id,
        cod_produto:           l.cod_produto,
        quantidade:            l.quantidade,
        data_retorno:          l.data_retorno,
        lote_retorno:          l.lote_retorno || null,
        filial:                l.filial || null,
      }));
      for (let i = 0; i < rows.length; i += 200) {
        const { error } = await supabase.from("lubpress_retornos_rateio")
          .insert(rows.slice(i, i + 200)); // insert puro — é ledger, nunca upsert/edita
        if (error) throw error;
      }
      await carregar();
    } catch (e) {
      setErroSalvar(e.message);
      throw e;
    } finally {
      setSalvando(false);
    }
  }
  // ── Registrar uma geração de documento (romaneio/embarque/etiqueta) ──
  // Ledger puro: sempre INSERT, nunca sobrescreve histórico anterior.
  async function salvarGeracao({ grupoChave, tipo, filial, dataRef, numNfs, geradoPor }) {
    const row = {
      id: `ger-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      grupo_chave: grupoChave,
      tipo, filial,
      data_ref: dataRef,
      num_nfs: numNfs || null,
      gerado_em: new Date().toISOString(),
      gerado_por: geradoPor || null,
    };
    const { error } = await supabase.from("lubpress_geracoes").insert(row);
    if (error) throw error;
    setGeracoesSt(prev => [row, ...prev]); // atualiza cache local sem recarregar tudo
  }

  // ── Alternar seleção de processo em lote (checkbox da LubNotas) ──────
  // Update direto e leve — não usa salvarNotas para não reenviar o
  // payload inteiro da NF (itens, endereço etc) só pra mudar um boolean.
  // Em lotes de 150 IDs por vez — com muitos IDs de uma vez o .in() gera
  // uma URL longa demais e o Supabase/Cloudflare devolve 400 Bad Request.
  async function atualizarSelecaoNotas(ids, selecionado, usuario) {
    if (!ids.length) return;
    const agora = new Date().toISOString();
    for (let i = 0; i < ids.length; i += 150) {
      const lote = ids.slice(i, i + 150);
      const { error } = await supabase.from("lubpress_notas")
        .update({
          selecionada_processo: selecionado,
          selecionada_em: agora,
          selecionada_por: usuario || null,
        })
        .in("id", lote);
      if (error) throw error;
    }
    setNotas(prev => prev.map(n =>
      ids.includes(n.id) ? { ...n, selecionada_processo: selecionado } : n));
  }

  return {
    geracoes, salvarGeracao,
    atualizarSelecaoNotas,
    tabelasVibra, setVibraTabelas, rotasVibra, setRotasVibra, ctes,
    notas, salvarNotas,
    tabelasLub, setTabelas: setTabelasLub,
    municipiosLub, setMunicipiosLub,
    clientes, setClientes,
    transportadoras, setTransportadoras,
    produtos,      setProdutos:    setProdutosLub,
    configPaletes, paletizados,
    tabelasGamma, municipiosGamma,
    armazenagemNotas, salvarArmazenagemNotas,
    retornosRateio,   salvarRetornosRateio,
    setCtes, salvarCte, salvarCtes, excluirCte,
    loading, erro, salvando, erroSalvar,
    recarregar: carregar,
  };
}